import {
  Add as AddIcon,
  Article as ArticleIcon,
  Headphones as AudioIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  Psychology as PsychologyIcon,
  Build as ToolIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useResources } from "../../hooks/useRedux";
import { Resource } from "../../store/slices/types";

const Resources: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const {
    resources,
    loading,
    error,
    deleteResource,
    pagination,
  } = useResources(paginationModel.page + 1, paginationModel.pageSize);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Note: Server-side pagination means filtering should be done on the server
  // For now, we'll keep client-side filtering for the stats, but the DataGrid uses server data
  const filteredResources = resources; // Server handles filtering

  // Calculate statistics
  const totalResources = resources.length;
  const articleResources = resources.filter((r) => r.type === "article").length;
  const videoResources = resources.filter((r) => r.type === "video").length;
  const audioResources = resources.filter((r) => r.type === "audio").length;

  const handleFilterClick = (category: string | null) => {
    setActiveFilter(category);
  };

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "article":
        return <ArticleIcon />;
      case "video":
        return <VideoIcon />;
      case "audio":
        return <AudioIcon />;
      case "pdf":
        return <PdfIcon />;
      case "link":
        return <LinkIcon />;
      case "tool":
        return <ToolIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const getCategoryColor = (resource: Resource) => {
    // First check if categoryDetails is populated from backend
    if (resource.categoryDetails?.color) {
      return resource.categoryDetails.color;
    }
    // Fallback to hardcoded colors based on category name
    const category = resource.category || resource.categoryDetails?.name || "";
    switch (category.toLowerCase()) {
      case "mindfulness":
        return "#667eea";
      case "stress-relief":
        return "#f093fb";
      case "sleep":
        return "#4facfe";
      case "anxiety":
        return "#fa709a";
      case "focus":
        return "#43e97b";
      case "relationships":
        return "#ff6b6b";
      default:
        return "#667eea";
    }
  };

  const getCategoryName = (resource: Resource) => {
    // First check if categoryDetails is populated from backend
    if (resource.categoryDetails) {
      return resource.categoryDetails.name || "Unknown";
    }
    return resource.category || "Unknown";
  };

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: "",
      flex: 0.1,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
          }}
        >
          {getTypeIcon(params.value)}
        </Avatar>
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2' fontWeight='600'>
            {params.value}
          </Typography>
          <Typography variant='caption' color='textSecondary' noWrap>
            {params.row.shortDescription?.substring(0, 50)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      flex: 0.5,
      minWidth: 80,
      filterable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size='small'
          color='info'
          variant='outlined'
        />
      ),
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => {
        const categoryName = getCategoryName(params.row);
        const categoryColor = getCategoryColor(params.row);
        return (
          <Chip
            label={categoryName}
            size='small'
            sx={{
              backgroundColor: categoryColor,
              color: "white",
              "&:hover": {
                backgroundColor: categoryColor,
                opacity: 0.8,
              },
            }}
          />
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value ? `${params.value} min` : "N/A"}
        </Typography>
      ),
    },
    {
      field: "viewCount",
      headerName: "Views",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant='body2' fontWeight='600'>
            {params.value ? params.value.toFixed(1) : "N/A"}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            ⭐
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Resource'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Resource'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/resources/add");
  };

  const handleEdit = (resource: Resource) => {
    const id = resource._id || resource.id;
    if (id) {
      navigate(`/resources/edit/${id}`);
    }
  };

  const handleDelete = async (id: string) => {
    const resource = resources.find((r) => r.id === id);
    const resourceTitle = resource?.title || "Resource";

    if (
      window.confirm(
        `Are you sure you want to delete "${resourceTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteResource(id);
        toast.success("resource is deleted");
      } catch (error) {
        console.error("Failed to delete resource:", error);
        toast.error(
          `❌ Failed to delete resource: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };


  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Resource Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Create and manage wellness resources
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                },
              }}
            >
              Create Resource
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  activeFilter === null
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: activeFilter === null ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === null
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick(null)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {totalResources}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Resources
                    </Typography>
                  </Box>
                  <PsychologyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "article" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "article"
                    ? "0 8px 25px rgba(33, 150, 243, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "article"
                    ? "2px solid #2196f3"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(33, 150, 243, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("article")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='info.main'
                    >
                      {articleResources}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Articles
                    </Typography>
                  </Box>
                  <ArticleIcon sx={{ fontSize: 40, color: "info.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "video" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "video"
                    ? "0 8px 25px rgba(156, 39, 176, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "video"
                    ? "2px solid #9c27b0"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(156, 39, 176, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("video")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='secondary.main'
                    >
                      {videoResources}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Videos
                    </Typography>
                  </Box>
                  <VideoIcon sx={{ fontSize: 40, color: "secondary.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "audio" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "audio"
                    ? "0 8px 25px rgba(255, 152, 0, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "audio"
                    ? "2px solid #ff9800"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(255, 152, 0, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("audio")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {audioResources}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Audio
                    </Typography>
                  </Box>
                  <AudioIcon sx={{ fontSize: 40, color: "warning.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Category Filters */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant='h6' fontWeight='600'>
            Filter by Category
          </Typography>
          {activeFilter && (
            <Button
              variant='text'
              size='small'
              onClick={() => handleFilterClick(null)}
              sx={{
                textTransform: "none",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              Clear All Filters
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {[
            "mindfulness",
            "stress-relief",
            "sleep",
            "anxiety",
            "focus",
            "relationships",
            "general",
          ].map((category) => (
            <Chip
              key={category}
              label={category}
              onClick={() => handleFilterClick(category)}
              variant={activeFilter === category ? "filled" : "outlined"}
              color={activeFilter === category ? "primary" : "default"}
              sx={{
                cursor: "pointer",
                textTransform: "capitalize",
                "&:hover": {
                  backgroundColor:
                    activeFilter === category ? "primary.main" : "action.hover",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Showing{" "}
            {activeFilter === "article" ||
            activeFilter === "video" ||
            activeFilter === "audio" ||
            activeFilter === "pdf" ||
            activeFilter === "link" ||
            activeFilter === "tool"
              ? `${activeFilter} type`
              : `${activeFilter} category`}{" "}
            resources:
          </Typography>
          <Chip
            label={activeFilter}
            color='primary'
            size='small'
            onDelete={() => handleFilterClick(null)}
          />
        </Box>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredResources}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={pagination?.total || 0}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Resources;
